import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import useLocation from 'hooks/useLocation';
import WeatherMapSegmentedControl from '../../components/WeatherMapSegmentedControl/WeatherMapSegmentedControl';
import '../../features/Hurricanes/assets/hurricanes.scss';
import HurricaneDataStatus from '../../features/Hurricanes/components/HurricaneDataStatus/HurricaneDataStatus';
import HurricaneMapContent from '../../features/Hurricanes/components/HurricaneMapContent/HurricaneMapContent';
import StormDetailsPanel from '../../features/Hurricanes/components/StormDetailsPanel/StormDetailsPanel';
import StormSelector from '../../features/Hurricanes/components/StormSelector/StormSelector';
import {
  HURRICANES_DATASET_OPTIONS,
  HURRICANES_DATASETS,
  HURRICANES_REFRESH_INTERVAL_MINUTES,
  HURRICANES_ROUTE,
  HURRICANES_WIDGET_ACCESSIBLE_NAME,
} from '../../features/Hurricanes/config/hurricanes.config';
import useHurricaneData from '../../features/Hurricanes/hooks/useHurricaneData';
import useStormReplay from '../../features/Hurricanes/hooks/useStormReplay';
import useStormSelection from '../../features/Hurricanes/hooks/useStormSelection';
import { toLatLng } from '../../features/Hurricanes/utils/hurricaneGeometry.utils';
import { getStormRecordAtFrame } from '../../features/Hurricanes/utils/hurricanes.utils';
import { resolveWeatherMapWidgetLayout } from '../../utils/weatherMapWidgetLayout.utils';
import WeatherMapWidgetEmptyState from '../components/WeatherMapWidgetEmptyState/WeatherMapWidgetEmptyState';
import {
  HURRICANES_WIDGET_COMPACT_MAX_WIDTH_PX,
  HURRICANES_WIDGET_LAYOUT_CONFIG,
  HURRICANES_WIDGET_REPLAY_TIMESTAMP_MIN_WIDTH_PX,
  HURRICANES_WIDGET_STACKED_MAX_WIDTH_PX,
} from './config/hurricanesWidget.config';
import StormReplayBar from './components/StormReplayBar/StormReplayBar';

// Dashboard widget: compact toolbar (storm selector + dataset toggle), map,
// details pane on the larger layouts, and a single-row replay bar when one
// storm is selected. Layout resolves from the grid span plus the measured
// cell width so narrow (mobile) cells stack instead of splitting.
const HurricanesWidget = ({ layout, dimensions, containerSizePx }) => {
  const { locations } = useLocation();
  const effectiveLocation = locations.effectiveLocation;
  const hurricaneData = useHurricaneData();
  const {
    liveStormGroups,
    historicalStormGroups,
    hasLiveStorms,
    isLiveLoading,
    isHistoricalLoading,
    liveError,
    historicalError,
    isLoading,
    isRefreshing,
    isError,
    refetch,
  } = hurricaneData;

  const {
    dataset,
    stormGroups,
    selectedStormKey,
    selectedStorm,
    visibleStormGroups,
    selectStorm,
    setDatasetToHistorical,
  } = useStormSelection({ liveStormGroups, historicalStormGroups, hasLiveStorms });

  const replay = useStormReplay({
    frameCount: selectedStorm?.records.length ?? 0,
    resetKey: selectedStormKey,
  });
  const activeRecord = getStormRecordAtFrame(selectedStorm?.records, replay.frameIndex);

  const containerWidth = containerSizePx?.width ?? null;
  const resolvedLayout = resolveWeatherMapWidgetLayout({
    layout,
    containerWidthPx: containerWidth,
    stackedMaxWidthPx: HURRICANES_WIDGET_STACKED_MAX_WIDTH_PX,
  });
  const widgetLayoutConfig = HURRICANES_WIDGET_LAYOUT_CONFIG[resolvedLayout];
  const isCompactWidth =
    containerWidth !== null && containerWidth < HURRICANES_WIDGET_COMPACT_MAX_WIDTH_PX;
  const showReplayTimestamp =
    containerWidth === null || containerWidth >= HURRICANES_WIDGET_REPLAY_TIMESTAMP_MIN_WIDTH_PX;
  const isDatasetToggleDisabled =
    !hasLiveStorms || isLiveLoading || isHistoricalLoading || liveError || historicalError;

  if (isLoading) {
    return <WidgetLoadingAnimation widgetName="HurricanesTropicalStorms" dimensions={dimensions} />;
  }

  if (isError) {
    return (
      <section aria-label={HURRICANES_WIDGET_ACCESSIBLE_NAME}>
        <ErrorMessage message="Unable to load hurricane data." onRetry={refetch} />
      </section>
    );
  }

  if (!liveStormGroups.length && !historicalStormGroups.length) {
    return (
      <WeatherMapWidgetEmptyState
        ariaLabel={HURRICANES_WIDGET_ACCESSIBLE_NAME}
        title="Hurricanes & Tropical Storms"
        message="No hurricane or tropical storm data is available right now."
        className="hurricanes hurricanes-widget"
      />
    );
  }

  return (
    <section
      aria-label={HURRICANES_WIDGET_ACCESSIBLE_NAME}
      className="hurricanes hurricanes-widget d-flex flex-column h-100 w-100 min-h-0 overflow-hidden gap-1"
    >
      <WidgetTimeUpdate
        route={HURRICANES_ROUTE}
        updateIntervalBasis={HURRICANES_REFRESH_INTERVAL_MINUTES}
        compact
      />

      <div className="d-flex flex-wrap align-items-center gap-2 min-w-0 flex-shrink-0">
        <div className="flex-grow-1 min-w-0">
          <StormSelector
            stormGroups={stormGroups}
            selectedStormKey={selectedStormKey}
            onSelectStorm={selectStorm}
            includeAllOption={dataset === HURRICANES_DATASETS.live}
            label={null}
            className="hurricanes-storm-select w-100"
          />
        </div>
        {widgetLayoutConfig.showDatasetToggle && (
          <WeatherMapSegmentedControl
            ariaLabel="Storm dataset"
            options={HURRICANES_DATASET_OPTIONS}
            value={dataset}
            onChange={(nextDataset) =>
              setDatasetToHistorical(nextDataset === HURRICANES_DATASETS.historical)
            }
            disabled={isDatasetToggleDisabled}
            fullWidth={false}
            className="flex-shrink-0"
            buttonClassName={`text-nowrap py-1 ${isCompactWidth ? 'px-2' : 'px-3'}`}
          />
        )}
        <HurricaneDataStatus
          liveError={liveError}
          historicalError={historicalError}
          isRefreshing={isRefreshing}
        />
      </div>

      <div className={widgetLayoutConfig.contentClassName}>
        <div className={widgetLayoutConfig.mapClassName} style={widgetLayoutConfig.mapStyle}>
          <HurricaneMapContent
            stormGroups={visibleStormGroups}
            selectedStorm={selectedStorm}
            dataset={dataset}
            frameIndex={selectedStorm ? replay.frameIndex : null}
            fallbackCenter={toLatLng(
              effectiveLocation?.coords?.[0],
              effectiveLocation?.coords?.[1],
            )}
          />
        </div>
        {widgetLayoutConfig.showInformationPanel && (
          <div
            className={widgetLayoutConfig.informationClassName}
            style={widgetLayoutConfig.informationStyle}
          >
            <StormDetailsPanel
              storm={selectedStorm}
              stormGroups={stormGroups}
              activeRecord={activeRecord}
              onSelectStorm={selectStorm}
            />
          </div>
        )}
      </div>

      {selectedStorm && (
        <StormReplayBar
          storm={selectedStorm}
          replay={replay}
          showSpeeds={widgetLayoutConfig.showPlaybackSpeedControl && !isCompactWidth}
          showTimestamp={showReplayTimestamp}
        />
      )}
    </section>
  );
};

export default HurricanesWidget;
